/**
 * Auth Hooks
 *
 * TanStack Query mutations and queries for authentication.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, type AuthUser } from '../stores/authStore';
import { authApi, ApiClientError } from '../lib/api';

/**
 * Hook for login mutation
 */
export function useLogin() {
  const setAuth = useAuthStore((state) => state.setAuth);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      rememberMe = false,
    }: {
      email: string;
      password: string;
      rememberMe?: boolean;
    }) => {
      return authApi.login(email, password, rememberMe);
    },
    onSuccess: (data) => {
      setAuth(data.user, data.accessToken);
      navigate('/');
    },
  });
}

/**
 * Hook for registration mutation
 */
export function useRegister() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      name,
    }: {
      email: string;
      password: string;
      name: string;
    }) => {
      return authApi.register(email, password, name);
    },
    onSuccess: () => {
      // After successful registration, redirect to login
      navigate('/login', {
        state: { message: 'Registration successful! Please log in.' }
      });
    },
  });
}

/**
 * Hook for logout mutation
 */
export function useLogout() {
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      await authApi.logout();
    },
    onSettled: () => {
      // Always clear auth state, even if API call fails
      clearAuth();
      queryClient.clear();
      navigate('/login');
    },
  });
}

/**
 * Hook for getting current user
 */
export function useCurrentUser() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setAuth = useAuthStore((state) => state.setAuth);
  const accessToken = useAuthStore((state) => state.accessToken);

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const user = await authApi.getCurrentUser();
      if (user && accessToken) {
        setAuth(user, accessToken);
      }
      return user;
    },
    enabled: isAuthenticated && !!accessToken,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });
}

/**
 * Hook for checking and initializing auth state
 */
export function useInitAuth() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setLoading = useAuthStore((s) => s.setLoading);

  return useMutation({
    mutationFn: async () => {
      // Try to refresh the token on mount
      const newToken = await authApi.refreshToken();

      if (newToken) {
        // Got a new token, fetch user info
        const currentUser = await authApi.getCurrentUser();
        if (currentUser) {
          return { user: currentUser, accessToken: newToken };
        }
      }

      return null;
    },
    onSuccess: (data) => {
      if (data) {
        setAuth(data.user, data.accessToken);
      } else if (!user || !isAuthenticated) {
        clearAuth();
      }
      setLoading(false);
    },
    onError: () => {
      clearAuth();
      setLoading(false);
    },
  });
}

/**
 * Get auth state selectors
 */
export function useAuthState() {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  return { user, isAuthenticated, isLoading };
}

export { ApiClientError };
export type { AuthUser };
